import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { 
  Plus, 
  Tags,
  Pencil,
  Trash2,
  Loader2,
  Smile
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import { DeletionPinDialog } from "../components/DeletionPinDialog";
import { useDeletionPin } from "../hooks/useDeletionPin";

// Fashion/Retail category icons (ZARA/H&M style)
const SUGGESTED_ICONS = [
  // Vêtements
  { icon: "👕", label: "T-shirts" },
  { icon: "👔", label: "Chemises" },
  { icon: "👗", label: "Robes" },
  { icon: "👖", label: "Pantalons" },
  { icon: "🩳", label: "Shorts" },
  { icon: "👙", label: "Maillots" },
  { icon: "🧥", label: "Manteaux" },
  { icon: "🧤", label: "Gants" },
  { icon: "🧣", label: "Écharpes" },
  { icon: "🧢", label: "Casquettes" },
  { icon: "👒", label: "Chapeaux" },
  { icon: "🎩", label: "Accessoires" },
  // Chaussures
  { icon: "👟", label: "Sneakers" },
  { icon: "👠", label: "Talons" },
  { icon: "👞", label: "Chaussures" },
  { icon: "👢", label: "Bottes" },
  { icon: "🥿", label: "Ballerines" },
  { icon: "👡", label: "Sandales" },
  // Accessoires
  { icon: "👜", label: "Sacs" },
  { icon: "👛", label: "Portefeuilles" },
  { icon: "🎒", label: "Sacs à dos" },
  { icon: "💼", label: "Business" },
  { icon: "🧳", label: "Bagages" },
  // Bijoux
  { icon: "💍", label: "Bagues" },
  { icon: "💎", label: "Bijoux" },
  { icon: "📿", label: "Colliers" },
  { icon: "⌚", label: "Montres" },
  { icon: "🕶️", label: "Lunettes" },
  // Autres
  { icon: "🎀", label: "Accessoires" },
  { icon: "🧴", label: "Beauté" },
  { icon: "💄", label: "Maquillage" },
  { icon: "🌸", label: "Parfums" },
  // Alimentaire (pour épicerie)
  { icon: "🍌", label: "Fruits" },
  { icon: "🥬", label: "Légumes" },
  { icon: "🌶️", label: "Épices" },
  { icon: "🥤", label: "Boissons" },
  { icon: "🥫", label: "Conserves" },
  { icon: "🧀", label: "Produits laitiers" },
  { icon: "🥖", label: "Boulangerie" },
  { icon: "🥩", label: "Viande" },
  { icon: "🐟", label: "Poisson" },
  { icon: "🍪", label: "Snacks" },
  // Génériques
  { icon: "📦", label: "Général" },
  { icon: "🏷️", label: "Étiquettes" },
  { icon: "⭐", label: "Favoris" },
  { icon: "🔥", label: "Tendances" },
  { icon: "💯", label: "Best-sellers" },
  { icon: "🆕", label: "Nouveautés" },
];

const initialFormState = {
  name: "",
  description: "",
  icon: "📦"
};

export default function Categories() {
  const { getAuthHeaders } = useAuth();
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState(initialFormState);

  const fetchData = async () => {
    try {
      const [categoriesRes, productsRes] = await Promise.all([
        axios.get(`${API}/categories`, getAuthHeaders()),
        axios.get(`${API}/products`, getAuthHeaders())
      ]);
      setCategories(categoriesRes.data);
      setProducts(productsRes.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des données");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const getProductCount = (categoryId) => {
    return products.filter(p => p.category_id === categoryId).length;
  };

  const handleOpenDialog = (category = null) => {
    if (category) {
      setEditingCategory(category);
      setFormData({
        name: category.name,
        description: category.description || "",
        icon: category.icon || "📦"
      });
    } else {
      setEditingCategory(null);
      setFormData(initialFormState);
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingCategory(null);
    setFormData(initialFormState);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error("Le nom de la catégorie est obligatoire");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        icon: formData.icon || "📦"
      };

      if (editingCategory) {
        await axios.put(`${API}/categories/${editingCategory.id}`, payload, getAuthHeaders());
        toast.success("Catégorie modifiée avec succès");
      } else {
        await axios.post(`${API}/categories`, payload, getAuthHeaders());
        toast.success("Catégorie ajoutée avec succès");
      }
      
      handleCloseDialog();
      fetchData();
    } catch (error) {
      let message = "Erreur lors de l'enregistrement";
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'string') {
          message = detail;
        } else if (Array.isArray(detail)) {
          message = detail.map(d => d.msg || d).join(', ');
        } else if (typeof detail === 'object' && detail.msg) {
          message = detail.msg;
        }
      }
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (pin) => {
    if (!categoryToDelete) return;
    const headers = { ...(getAuthHeaders().headers || {}) };
    if (pin) headers["X-Deletion-Pin"] = pin;
    await axios.delete(`${API}/categories/${categoryToDelete.id}`, { headers });
    toast.success("Catégorie supprimée");
    setDeleteDialogOpen(false);
    setCategoryToDelete(null);
    fetchData();
  };

  const deletionPin = useDeletionPin(handleDelete);

  const requestDelete = async () => {
    try {
      await deletionPin.runDelete();
    } catch (error) {
      let message = "Erreur lors de la suppression";
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'string') {
          message = detail;
        } else if (Array.isArray(detail)) {
          message = detail.map(d => d.msg || d).join(', ');
        } else if (typeof detail === 'object' && detail.msg) {
          message = detail.msg;
        }
      }
      toast.error(message);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-pulse flex flex-col items-center gap-4">
          <div className="w-16 h-16 rounded-full tropical-gradient"></div>
          <p className="text-muted-foreground">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in" data-testid="categories-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold font-outfit text-foreground">Catégories</h1>
          <p className="text-muted-foreground mt-1">{categories.length} catégorie(s)</p>
        </div>
        <Button 
          onClick={() => handleOpenDialog()} 
          className="btn-primary"
          data-testid="add-category-btn"
        >
          <Plus className="w-5 h-5 mr-2" />
          Ajouter une catégorie
        </Button>
      </div>

      {/* Categories Grid */}
      {categories.length === 0 ? (
        <Card className="border-border/50">
          <CardContent className="p-12 text-center">
            <div className="w-16 h-16 rounded-full bg-secondary flex items-center justify-center mx-auto mb-4">
              <Tags className="w-8 h-8 text-secondary-foreground" />
            </div>
            <p className="text-muted-foreground">Aucune catégorie trouvée</p>
            <Button 
              onClick={() => handleOpenDialog()} 
              className="mt-4 btn-primary"
              data-testid="add-first-category-btn"
            >
              <Plus className="w-5 h-5 mr-2" />
              Ajouter votre première catégorie
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {categories.map((category) => {
            const productCount = getProductCount(category.id);
            return (
              <Card 
                key={category.id} 
                className="card-product group"
                data-testid={`category-card-${category.id}`}
              >
                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center text-3xl">
                      {category.icon || "📦"}
                    </div>
                    <div className="flex gap-1">
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => handleOpenDialog(category)}
                        data-testid={`edit-category-${category.id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => { setCategoryToDelete(category); setDeleteDialogOpen(true); }}
                        className="text-destructive hover:text-destructive hover:bg-destructive/10"
                        data-testid={`delete-category-${category.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  
                  <h3 className="font-semibold text-lg text-foreground mb-1">{category.name}</h3>
                  
                  {category.description && (
                    <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{category.description}</p>
                  )}
                  
                  <div className="flex items-center justify-between mt-4 pt-3 border-t border-border/50">
                    <span className="text-sm text-muted-foreground">
                      {productCount} produit{productCount !== 1 ? 's' : ''}
                    </span>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[500px]" data-testid="category-dialog">
          <DialogHeader>
            <DialogTitle className="font-outfit text-xl">
              {editingCategory ? "Modifier la catégorie" : "Ajouter une catégorie"}
            </DialogTitle>
            <DialogDescription>
              {editingCategory ? "Modifiez les informations de la catégorie" : "Créez une nouvelle catégorie pour organiser vos produits"}
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Icon Selector */}
            <div className="space-y-2">
              <Label>Icône de la catégorie</Label>
              <div className="flex items-center gap-3">
                <div className="w-16 h-16 rounded-xl bg-primary/10 flex items-center justify-center text-4xl border-2 border-dashed border-primary/30">
                  {formData.icon}
                </div>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button type="button" variant="outline" className="flex-1" data-testid="select-icon-btn">
                      <Smile className="w-4 h-4 mr-2" />
                      Choisir une icône
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-80 p-3" align="start">
                    <div className="space-y-3">
                      <p className="text-sm font-medium text-muted-foreground">Icônes suggérées</p>
                      <div className="grid grid-cols-8 gap-1">
                        {SUGGESTED_ICONS.map(({ icon, label }) => (
                          <button
                            key={icon}
                            type="button"
                            title={label}
                            onClick={() => setFormData({ ...formData, icon })}
                            className={`w-8 h-8 flex items-center justify-center text-lg rounded-md hover:bg-secondary transition-colors ${formData.icon === icon ? 'bg-primary/20 ring-2 ring-primary' : ''}`}
                            data-testid={`icon-${icon}`}
                          >
                            {icon}
                          </button>
                        ))}
                      </div>
                      <div className="pt-2 border-t">
                        <Label className="text-xs text-muted-foreground">Ou saisissez un emoji</Label>
                        <Input
                          value={formData.icon}
                          onChange={(e) => setFormData({ ...formData, icon: e.target.value.slice(-2) })}
                          placeholder="📦"
                          className="mt-1 text-center text-xl"
                          maxLength={2}
                          data-testid="custom-icon-input"
                        />
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="name">Nom de la catégorie *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="Ex: Robes, T-shirts, Accessoires..."
                data-testid="category-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description (optionnel)</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Description de la catégorie..."
                rows={3}
                data-testid="category-description-input"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseDialog} data-testid="cancel-category-btn">
                Annuler
              </Button>
              <Button type="submit" className="btn-primary" disabled={saving} data-testid="save-category-btn">
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Enregistrement...
                  </>
                ) : (
                  editingCategory ? "Modifier" : "Ajouter"
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent data-testid="delete-category-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer cette catégorie ?</AlertDialogTitle>
            <AlertDialogDescription>
              {getProductCount(categoryToDelete?.id) > 0 ? (
                <span className="text-destructive">
                  Impossible de supprimer "{categoryToDelete?.name}". 
                  <br />Cette catégorie contient encore {getProductCount(categoryToDelete?.id)} produit(s).
                  <br />Supprimez ou déplacez d'abord ces produits.
                </span>
              ) : (
                <span>
                  Êtes-vous sûr de vouloir supprimer la catégorie "{categoryToDelete?.name}" ?
                  <br />Cette action est irréversible.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-category-btn">Annuler</AlertDialogCancel>
            <AlertDialogAction 
              onClick={requestDelete}
              className="bg-destructive hover:bg-destructive/90"
              disabled={getProductCount(categoryToDelete?.id) > 0}
              data-testid="confirm-delete-category-btn"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* [DELETION-PIN] Owner PIN required for team member deletion */}
      <DeletionPinDialog
        open={deletionPin.open}
        onClose={deletionPin.cancel}
        onSubmit={(pin) => deletionPin.submitPin(pin).catch((err) => {
          toast.error(err?.response?.data?.detail || "Erreur lors de la suppression");
        })}
        loading={deletionPin.loading}
        error={deletionPin.error}
        title={`Supprimer « ${categoryToDelete?.name || 'cette catégorie'} »`}
        description="La suppression d'une catégorie requiert le code PIN du propriétaire de la boutique."
      />
    </div>
  );
}
